import { Key } from "@zapier/design-system";
import { createConnectionShareFromId } from "./createConnectionShareFromId";
import { ConnectionShare } from "./useFormatConnectionShares";
import { ConnectionShareType } from "../../types";

export interface PendingShares {
  toAdd: ConnectionShare[];
  toDelete: ConnectionShare[];
}

export const noPendingShares = (): PendingShares => ({
  toAdd: [],
  toDelete: [],
});

const isSameShare = (
  share: ConnectionShare,
  id: Key,
  shareType: ConnectionShareType,
) => String(share.id) === String(id) && share.shareType === shareType;

export const findShare = (
  id: Key,
  shareType: ConnectionShareType,
  shares: ConnectionShare[],
) => shares.find((share) => isSameShare(share, id, shareType));

const withoutShare = (
  shares: ConnectionShare[],
  id: Key,
  shareType: ConnectionShareType,
) => shares.filter((share) => !isSameShare(share, id, shareType));

const withoutEveryone = (shares: ConnectionShare[]) =>
  withoutShare(shares, "all", ConnectionShareType.All);

const everyoneShare = () =>
  createConnectionShareFromId("all", ConnectionShareType.All);

function selectEveryone(shares: ConnectionShare[]): PendingShares {
  const isAlreadyShared = findShare("all", ConnectionShareType.All, shares);
  return {
    toAdd: isAlreadyShared ? [] : [everyoneShare()],
    toDelete: shares.filter(
      ({ shareType: type }) =>
        type !== ConnectionShareType.Owner && type !== ConnectionShareType.All,
    ),
  };
}

function restoreShare(
  pending: PendingShares,
  id: Key,
  shareType: ConnectionShareType,
): PendingShares {
  return {
    toAdd: withoutEveryone(pending.toAdd),
    toDelete: withoutShare(pending.toDelete, id, shareType),
  };
}

function queueShare(
  pending: PendingShares,
  shares: ConnectionShare[],
  id: Key,
  shareType: ConnectionShareType,
): PendingShares {
  const toDelete = withoutShare(pending.toDelete, id, shareType);
  if (findShare(id, shareType, pending.toAdd)) return { ...pending, toDelete };

  const toAdd = [
    ...pending.toAdd,
    createConnectionShareFromId(Number(id), shareType),
  ];
  const currentEveryone = findShare("all", ConnectionShareType.All, shares);
  const isEveryoneQueuedForDelete = findShare(
    "all",
    ConnectionShareType.All,
    toDelete,
  );
  if (!currentEveryone || isEveryoneQueuedForDelete) {
    return { toAdd: withoutEveryone(toAdd), toDelete };
  }
  return {
    toAdd: withoutEveryone(toAdd),
    toDelete: [...toDelete, everyoneShare()],
  };
}

/**
 * Next pending state after a share is selected. Selecting Everyone replaces
 * all members and teams, and selecting anything else replaces Everyone.
 */
export function addPendingShare(
  pending: PendingShares,
  shares: ConnectionShare[],
  id: Key,
  shareType: ConnectionShareType,
): PendingShares {
  if (id === "all") return selectEveryone(shares);

  const isRemoved = findShare(id, shareType, pending.toDelete);
  if (findShare(id, shareType, shares) && isRemoved) {
    return restoreShare(pending, id, shareType);
  }
  return queueShare(pending, shares, id, shareType);
}

/** Next pending state after a share is removed. Only saved shares are queued for deletion. */
export function removePendingShare(
  pending: PendingShares,
  shares: ConnectionShare[],
  id: Key,
  shareType: ConnectionShareType,
): PendingShares {
  const currentShare = findShare(id, shareType, shares);
  const isQueued = findShare(id, shareType, pending.toDelete);
  return {
    toAdd: withoutShare(pending.toAdd, id, shareType),
    toDelete:
      currentShare && !isQueued
        ? [...pending.toDelete, currentShare]
        : pending.toDelete,
  };
}
